<template>
    <jet-form-section @submitted="enableSmsAlerts">
        <template #title>
            {{ trans('profile.sms_notifications.header') }}
        </template>

        <template #form>
            <p class="mb-4">
                {{ trans('profile.sms_notifications.description') }}
            </p>

            <p class="mb-2">{{ trans('profile.sms_notifications.notification_info') }}</p>

            <Markdown class="mb-4 ps-5" :content="trans('profile.sms_notifications.notification_types.description')" list-style="disc" />

            <div class="grid w-full grid-cols-12 items-center gap-4">
                <div class="col-span-3">
                    <p class="font-semibold">
                        {{ trans('profile.sms_notifications.label.phone_number') }}
                        <br />
                        <span class="text-sm font-light text-muted">
                            {{ trans('profile.sms_notifications.label.phone_number_hint') }}
                        </span>
                    </p>
                </div>
                <jet-input
                    v-model="form.phoneNumber"
                    :invalidFeedback="form.errors.phoneNumber"
                    :placeholder="trans('profile.sms_notifications.placeholder.phone_number')"
                    :readonly="hasOptedIn"
                    autocomplete="tel"
                    class="col-span-9"
                    type="tel"
                />
            </div>

            <p>
                {{ trans('profile.sms_notifications.consent_text') }}
            </p>
        </template>

        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                {{ trans('general.message.saved') }}
            </jet-action-message>

            <jet-button v-if="hasOptedIn" :is-processing="isDisablingSmsAlerts" type="button" @click="disableSmsAlerts">
                {{ trans('profile.sms_notifications.action.disable') }}
            </jet-button>
            <jet-button v-else :disabled="!form.phoneNumber" :is-processing="form.processing">
                {{ trans('profile.sms_notifications.action.enable') }}
            </jet-button>
        </template>
    </jet-form-section>
</template>

<script setup>
import JetButton from '@/Jetstream/Button.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { visitModal } from '@/Utils/visitModal';
import { $http } from '@/bootstrap.js';
import { trans } from '@/mixins/i18n.js';
import { computed, ref } from 'vue';
import Markdown from '@/Jetstream/Markdown.vue';

const page = usePage();
const isDisablingSmsAlerts = ref(false);
const user = computed(() => page.props.user);
const hasOptedIn = computed(() => !!user.value?.phoneVerified);

const form = useForm({
    phoneNumber: user.value?.phoneNumber || '',
});

const enableSmsAlerts = async () => {
    form.clearErrors();

    try {
        form.processing = true;

        await $http.post(route('user.phone.verification-code.send'), {
            phoneNumber: form.phoneNumber,
        });

        await visitModal(route('user.phone.verify.modal'), {
            data: { phoneNumber: form.phoneNumber },
        });
    } catch (error) {
        const status = error.response?.status;

        if (status === 422) {
            // Transform Laravel's { field: ['msg1', 'msg2'] } to Inertia's { field: 'msg1' }
            const errors = error.response.data.errors || {};
            Object.keys(errors).forEach((key) => {
                form.setError(key, errors[key][0]);
            });
        } else {
            form.setError('phone_number', trans('general.error.generic'));
        }
    } finally {
        form.processing = false;
    }
};

const disableSmsAlerts = async () => {
    isDisablingSmsAlerts.value = true;

    try {
        await visitModal(route('user.phone.disable.modal'), {
            listeners: {
                numberDisabled() {
                    form.phoneNumber = '';
                },
            },
        });
    } finally {
        isDisablingSmsAlerts.value = false;
    }
};
</script>
